import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachFileOutlined,
  EmailOutlined,
  LockOutlined,
  NotificationsOutlined,
} from "@mui/icons-material";
import { getDatumZeit } from "@accredo/fe-shared/services/formatService.js";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import DocumentViewer from "../MDDForm/Viewer/DocumentViewer.jsx";
import { useClaimAttachmentActions } from "../../hooks/useClaimAttachmentActions.jsx";
import { DetailField, MultilineText } from "./DetailViewComponents.jsx";
import OutgoingMailStatusChip from "../mail/OutgoingMailStatusChip.jsx";
import { getEnv } from "@accredo/fe-shared/env.js";
import { createInsurerContactFromClaim } from "@accredo/fe-shared/services/insurerContactService.js";
import NewInsurerContactDialog from "./NewInsurerContactDialog.jsx";

export default function CommunicationDetailView({
  kommunikation,
  kunde,
  outgoingMails = [],
  onClose,
  onReload,
}) {
  const { findById } = useClaimAttachmentActions();
  const [viewerDocument, setViewerDocument] = useState(null);
  const [loadingAnlageId, setLoadingAnlageId] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  const handleOpenAnlage = async (anlage) => {
    setLoadingAnlageId(anlage.id);
    try {
      await findById(anlage.id, {
        setResult: (data) => {
          const attachment_data = data?.data ?? data;
          setViewerDocument({ ...attachment_data, success: true });
        },
      });
    } finally {
      setLoadingAnlageId(null);
    }
  };

  if (!kommunikation) return null;

  const handleCreateContact = async (values) => {
    await createInsurerContactFromClaim(kommunikation.schaden_id, values);
    await onReload?.();
  };

  const {
    betreff,
    from,
    from_name,
    from_insurer_contact,
    can_add_sender_as_insurer_contact,
    beschreibung,
    intern,
    an_versicherer,
    an_weitere,
    recipient_name,
    recipient_email,
    anlagen,
    created_by,
    created_at,
    sent_at,
    type,
  } = kommunikation;

  const isSystemMessage = type === "system_message";
  const hasRecipients =
    (an_weitere && an_weitere.length > 0) ||
    ["tenant_message", "customer_message", "insurer_email"].includes(type);
  const outgoingMail = outgoingMails[0] ?? null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
      {/* Meta row */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
        {intern && (
          <Chip
            icon={<LockOutlined sx={{ fontSize: "0.9rem" }} />}
            label="Intern"
            size="small"
            variant="outlined"
          />
        )}

        {!isSystemMessage && created_by && (
          <Typography variant="caption" color="text.secondary">
            Erstellt von {created_by.vorname} {created_by.nachname}
          </Typography>
        )}
        {created_at && (
          <Typography variant="caption" color="text.secondary">
            {getDatumZeit(created_at)}
          </Typography>
        )}
        {sent_at && (
          <Typography variant="caption" color="text.secondary">
            Gesendet: {getDatumZeit(sent_at)}
          </Typography>
        )}
        {outgoingMail &&
          (() => {
            const timestamp =
              outgoingMail.status === "failed"
                ? outgoingMail.failed_at
                : outgoingMail.sent_at;
            const label = timestamp
              ? getDatumZeit(timestamp)
              : outgoingMail.status === "pending"
                ? "Ausstehend"
                : null;
            return (
              <Tooltip title={label ?? ""} disableHoverListener={!label}>
                <span>
                  <OutgoingMailStatusChip status={outgoingMail.status} />
                </span>
              </Tooltip>
            );
          })()}
      </Stack>

      {/* Recipients */}

      {hasRecipients && (
        <DetailField label="Empfänger">
          {type === "tenant_message" && (
            <Box mr={2} mb={1}>
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <NotificationsOutlined fontSize="small" />
                    <span>
                      {kunde.display_name}
                    </span>
                  </Box>
                }
              />
            </Box>
          )}

          {type === "customer_message" && (
            <Box mr={2} mb={1}>
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <NotificationsOutlined fontSize="small" />
                    <span>
                      {getEnv("VITE_TENANT_LABEL")} Sachbearbeiter
                    </span>
                  </Box>
                }
              />
            </Box>
          )}

          {recipient_email && (
            <Box mr={2} mb={1}>
              <Chip
                size="small"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <EmailOutlined fontSize="small" />
                    <span>
                      {recipient_name
                        ? `${recipient_name} <${recipient_email}>`
                        : recipient_email}
                    </span>
                  </Box>
                }
              />
            </Box>
          )}

          {an_weitere && an_weitere.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {an_weitere
                .filter((email) => email !== recipient_email)
                .map((email, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <EmailOutlined fontSize="small" />
                        <span>{email}</span>
                      </Box>
                    }
                  />
                ))}
            </Stack>
          )}
        </DetailField>
      )}

      {/* Sender of imported mails */}
      {from && (
        <DetailField label="Von">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>
              {from_insurer_contact
                ? `${from_insurer_contact.display_name} <${from_insurer_contact.email}>`
                : from_name
                  ? `${from_name} <${from}>`
                  : from}
            </Typography>
            {can_add_sender_as_insurer_contact && (
              <DsButton
                variant="secondary"
                onClick={() => setContactDialogOpen(true)}
              >
                Kontakt hinzufügen
              </DsButton>
            )}
          </Stack>
        </DetailField>
      )}

      <Divider />

      {/* Subject */}
      <Tooltip title={"Betreff"}>
        <Typography variant="h3">{betreff}</Typography>
      </Tooltip>

      {/* Body */}
      <Box sx={{ maxWidth: "90ch" }}>
        <MultilineText value={beschreibung} />
      </Box>

      {/* Attachments */}
      {anlagen && anlagen.length > 0 && (
        <DetailField label="Anlagen">
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {anlagen.map((anlage) => (
              <Tooltip key={anlage.id} title="Anlage öffnen">
                <Chip
                  icon={
                    loadingAnlageId === anlage.id ? (
                      <CircularProgress size={12} sx={{ ml: 0.5 }} />
                    ) : (
                      <AttachFileOutlined
                        sx={{
                          fontSize: "1rem",
                        }}
                      />
                    )
                  }
                  label={anlage.bezeichnung || anlage.label || anlage.filename}
                  size="small"
                  variant="outlined"
                  onClick={() => handleOpenAnlage(anlage)}
                  disabled={loadingAnlageId !== null}
                  sx={{ cursor: "pointer" }}
                />
              </Tooltip>
            ))}
          </Stack>
        </DetailField>
      )}

      {/* Document viewer — rendered outside the detail box so it can open its own dialog */}
      {viewerDocument && (
        <DocumentViewer
          document={viewerDocument}
          onClose={() => setViewerDocument(null)}
        />
      )}

      {can_add_sender_as_insurer_contact && (
        <NewInsurerContactDialog
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          onCreate={handleCreateContact}
          defaultName={from_name ?? ""}
          defaultEmail={from}
        />
      )}

      {/* Close button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
        <DsButton variant="tertiary" onClick={onClose}>
          Schließen
        </DsButton>
      </Box>
    </Box>
  );
}

CommunicationDetailView.propTypes = {
  kommunikation: PropTypes.object,
  outgoingMails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string,
      sent_at: PropTypes.string,
      failed_at: PropTypes.string,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onReload: PropTypes.func,
};
